import { Observable } from "rxjs";
import { Injectable, inject } from "@angular/core";
import { CommonService } from "@sportyano/core/services/shard/common.service";
import { environment } from "src/environments/environment";
import { AcademyCardResponse } from "@sportyano/core/models/academy-card/academy-card.model";
import { SingleAcademyResponse } from "@sportyano/core/models/single-academy-details/single-academy.model";
import { HttpClient, HttpParams } from "@angular/common/http";
import { VerifyPlayerStatus } from "@sportyano/account/components/branches-management/player-list/models/const/verify-player-status";
import { IPaginatedResponse } from "@sportyano/core/models/response/response.interface";
import { PlayerProfile } from "@sportyano/core/models/player-card.model";
import { IVerifyPlayerPayload } from "@sportyano/account/components/branches-management/player-list/models/interface/verify-player-payload.interface";
import { PayloadUtils } from "@sportyano/shared/utilis/payload-utilits";
import { IProgramDetailsResponse  ,IPendingProgramDetailsResponse, ITournamentsDetailsPayload, ITopProgramsResponse} from "@sportyano/core/models/programs/program-details";
import { AuthService } from "@sportyano/core/services/authServices/auth.service";
import { permission, UserType } from "@sportyano/core/models/permissions/permission";

@Injectable({
	providedIn: "root",
})
export class AcademyService {
	// Inject
	private _httpClient = inject(HttpClient);
	private _authService = inject(AuthService);
	public userType: any = this._authService.getUserType();
	private academyBaseUrl = `${environment.baseUrl}academies/`;
	constructor(private commonServices: CommonService) {}

	getAcademies(params: any): Observable<AcademyCardResponse> {
		return this.commonServices.CommonGetWithParamRequests(
			`${environment.baseUrl}home-page/academies`,
			params
		);
	}

	getTournaments(params: any): Observable<ITournamentsDetailsPayload> {
		return this.commonServices.CommonGetWithParamRequests(
			`${environment.baseUrl}home-page/tournaments`,
			params
		);
	}

	public getSelectedAcademies() {
		return this.commonServices.CommonGetRequests(
			`${environment.baseUrl}home-page/main-page-academies`
		);
	}

	getSingleAcademy(id: string): Observable<SingleAcademyResponse> {
		return this.commonServices.CommonGetRequests(
			`${environment.baseUrl}home-page/academy/${id}`
		);
	}

	public getPlayerVerification(
		payload: Partial<IVerifyPlayerPayload>
	): Observable<IPaginatedResponse<PlayerProfile>> {
		const _payload = PayloadUtils.filterFalsyValues(payload);
		let params = new HttpParams();
		(Object.keys(_payload) as Array<keyof IVerifyPlayerPayload>).forEach((key) => {
			const value = _payload[key];
			if (value !== undefined && value !== null) {
				params = params.set(key as string, value.toString());
			}
		});
		return this._httpClient.get<IPaginatedResponse<PlayerProfile>>(
			`${environment.baseUrl}academies/list-academy-players`,
			{
				params
			}
		);
	}

	public updatePlayerVerification(payload: {
		playerId: number;
		state: VerifyPlayerStatus;
	}): Observable<{ message: string; success: boolean }> {
		return this._httpClient.put<{ message: string; success: boolean }>(
			`${environment.baseUrl}academies/update-request-player/${payload.playerId}`,
			{
				status: payload.state,
			}
		);
	}

	public getProgramDetails(
		id: string | null
	): Observable<IProgramDetailsResponse> {
		return this._httpClient.get<any>(
			`${environment.baseUrl}academies/programs/${id}`
		);
	}

	public getPendingProgramDetails(
		programId: string
	): Observable<any> {
		if (this.userType === permission.branch) {
			return this._httpClient.get<any>(
				`${environment.baseUrl}branch-user/pending-programs/${programId}`
			);
		}
		const url = `${environment.baseUrl}pending-programs/${programId}`;
		return this._httpClient.get<any>(url);
	}

	public getFederationProgramDetails(
		id: string | null
	): Observable<IProgramDetailsResponse> {
		return this._httpClient.get<any>(
			`${environment.baseUrl}federations/programs/${id}`
		);
	}

	public getPendingFederationProgramDetails(
		programId: string
	): Observable<any> {
		const url = `${environment.baseUrl}pending-federation-programs/${programId}`;
		return this._httpClient.get<any>(url);
	}

	public getTopPrograms(
	): Observable<ITopProgramsResponse> {
		return this.commonServices.CommonGetRequests(
			`${environment.baseUrl}home-page/main-page-programs`
		);
	}

	public getTopTournaments(
	): Observable<ITournamentsDetailsPayload> {
		return this.commonServices.CommonGetRequests(
			`${environment.baseUrl}home-page/main-page-tournament`
		);
	}



	// add-member-federation // 

	public getFederationList(): Observable<any> {
		return this._httpClient.get<any>(
			`${environment.baseUrl}academies/federations-list`
		);
	}

	public addMemberByIdToFederation(payload: { player_id: number; federation_id: number }): Observable<any> {
		return this._httpClient.post<any>(
			`${environment.baseUrl}academies/add-member-id-federation`,
			payload
		);
	}

	public getFederationMembers(subscriptionId: number): Observable<any> {
		return this._httpClient.get<any>(
			`${environment.baseUrl}academies/federation-members/${subscriptionId}`
		);
	}

	public addMemberToFederation(payload: any): Observable<any> {
		return this._httpClient.post<any>(
			`${this.academyBaseUrl}add-member-federation`,
			payload
		);
	}

	public uploadPlayerAttachment(playerId: any, payload: { path: string }): Observable<any> {
		return this._httpClient.post<any>(
			`${this.academyBaseUrl}add-attachment-player/${playerId}`,
			payload
		);
	}




	public getRenewalProgramId(programId: number): Observable<any> {
		return this._httpClient.get<any>(
			`${environment.baseUrl}academies/get-players-lists-federation/${programId}`
		);
	}

	public addAcademyPlayer(payload: FormData): Observable<any> {
		return this._httpClient.post<any>(
			`${environment.baseUrl}academies/add-academy-player`,
			payload
		);
	}

	public getTrackingPlayerPayments(payload: any): Observable<any> {
		return this._httpClient.post<any>(
			`${environment.baseUrl}academies/tracking-players-payments`,
			payload
		);
	}

	public markPaymentByAcademy(payload: any): Observable<any> {
		return this._httpClient.post<any>(
			`${environment.baseUrl}academies/make-payment-by-academy`,
			payload
		);
	}

	public getSmsConsumed(): Observable<any> {
		return this._httpClient.post<any>(
			`${environment.baseUrl}academies/sms-consumed`,
			{}
		);
	}

	public getAcademyLeads(): Observable<any> {
		return this._httpClient.get<any>(
			`${environment.baseUrl}academies/leads`
		);
	}

}
